
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const categories = [
  { name: 'Pets', href: '/pets' },
  { name: 'Pet Food', href: '/blog?category=pet-food' },
  { name: 'Pet Care', href: '/blog?category=pet-care' },
  { name: 'Pet Vitamins', href: '/blog?category=pet-vitamins' },
  { name: 'Pet Accessories', href: '/blog?category=pet-accessories' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isMobileCategoryOpen, setIsMobileCategoryOpen] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setIsCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBooking = () => {
    navigate('/booking');
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-2">
          <div className="flex items-start justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/8ba2fb49baf129fe47398873ffc1b622/ae217e5ef1f4a035b7b045b6b3280b3a.jpeg" 
                alt="Fluffy Pets Logo" 
                className="w-48 h-32 object-contain"
              />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-12 mt-2">
              <a
                href="#services"
                className="text-[28px] font-bold bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] bg-clip-text text-transparent hover:from-[#FF9BC8] hover:to-[#9B4C9A] transition-all cursor-pointer"
                style={{ fontFamily: 'Pacifico, cursive' }}
              >
                Services
              </a>
              <a
                href="#about"
                className="text-[28px] font-bold bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] bg-clip-text text-transparent hover:from-[#FF9BC8] hover:to-[#9B4C9A] transition-all cursor-pointer"
                style={{ fontFamily: 'Pacifico, cursive' }}
              >
                About
              </a>
              <a
                href="/testimonials"
                className="text-[28px] font-bold bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] bg-clip-text text-transparent hover:from-[#FF9BC8] hover:to-[#9B4C9A] transition-all cursor-pointer"
                style={{ fontFamily: 'Pacifico, cursive' }}
              >
                Reviews
              </a>

              {/* Pets Nav Link */}
              <a
                href="/pets"
                className="text-[28px] font-bold bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] bg-clip-text text-transparent hover:from-[#FF9BC8] hover:to-[#9B4C9A] transition-all cursor-pointer"
                style={{ fontFamily: 'Pacifico, cursive' }}
              >
                Pets
              </a>

              {/* Blog with Category Dropdown */}
              <div ref={categoryRef} className="relative">
                <button
                  onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                  className="text-[28px] font-bold bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] bg-clip-text text-transparent hover:from-[#FF9BC8] hover:to-[#9B4C9A] transition-all cursor-pointer flex items-center gap-1"
                  style={{ fontFamily: 'Pacifico, cursive' }}
                >
                  Blog
                  <i className={`ri-arrow-down-s-line text-[#9B4C9A] text-xl transition-transform duration-300 ${isCategoryOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {/* Dropdown */}
                {isCategoryOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 bg-white rounded-xl shadow-2xl border border-[#FF9BC8]/20 overflow-hidden animate-fade-in">
                    <div className="py-2">
                      <a
                        href="/blog"
                        className="block px-5 py-3 text-sm font-semibold text-[#9B4C9A] hover:bg-gradient-to-r hover:from-[#9B4C9A]/5 hover:to-[#FF9BC8]/10 transition-all cursor-pointer border-b border-[#FF9BC8]/10"
                        style={{ fontFamily: 'Nunito, sans-serif' }}
                      >
                        <span className="flex items-center gap-2">
                          <i className="ri-article-line w-5 h-5 flex items-center justify-center"></i>
                          All Posts
                        </span>
                      </a>
                      {categories.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.href}
                          onClick={() => setIsCategoryOpen(false)}
                          className="block px-5 py-3 text-sm font-semibold text-[#9B4C9A] hover:bg-gradient-to-r hover:from-[#9B4C9A]/5 hover:to-[#FF9BC8]/10 transition-all cursor-pointer"
                          style={{ fontFamily: 'Nunito, sans-serif' }}
                        >
                          <span className="flex items-center gap-2">
                            <i className={`${
                              cat.name === 'Pets' ? 'ri-bear-smile-line' :
                              cat.name === 'Pet Food' ? 'ri-restaurant-line' :
                              cat.name === 'Pet Care' ? 'ri-heart-pulse-line' :
                              cat.name === 'Pet Vitamins' ? 'ri-capsule-line' :
                              'ri-shopping-bag-line'
                            } w-5 h-5 flex items-center justify-center`}></i>
                            {cat.name}
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <a
                href="#contact"
                className="text-[28px] font-bold bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] bg-clip-text text-transparent hover:from-[#FF9BC8] hover:to-[#9B4C9A] transition-all cursor-pointer"
                style={{ fontFamily: 'Pacifico, cursive' }}
              >
                Contact
              </a>
            </div>

            {/* CTA Button */}
            <button
              onClick={handleBooking}
              className="hidden md:block bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] text-white px-8 py-3 rounded-full text-lg font-semibold hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap cursor-pointer mt-2"
            >
              Book Now
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-[#9B4C9A] text-3xl cursor-pointer"
            >
              <i className={isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="fixed inset-0 bg-gradient-to-br from-[#9B4C9A]/95 to-[#FF9BC8]/95 backdrop-blur-sm z-40 md:hidden">
          <div className="flex flex-col items-center justify-center h-full gap-6">
            <a
              href="#services"
              onClick={() => setIsMenuOpen(false)}
              className="text-[28px] font-bold text-white hover:scale-110 transition-all cursor-pointer"
              style={{ fontFamily: 'Pacifico, cursive' }}
            >
              Services
            </a>
            <a
              href="#about"
              onClick={() => setIsMenuOpen(false)}
              className="text-[28px] font-bold text-white hover:scale-110 transition-all cursor-pointer"
              style={{ fontFamily: 'Pacifico, cursive' }}
            >
              About
            </a>
            <a
              href="#reviews"
              onClick={() => setIsMenuOpen(false)}
              className="text-[28px] font-bold text-white hover:scale-110 transition-all cursor-pointer"
              style={{ fontFamily: 'Pacifico, cursive' }}
            >
              Reviews
            </a>

            {/* Mobile Blog with Categories */}
            <div className="flex flex-col items-center">
              <button
                onClick={() => setIsMobileCategoryOpen(!isMobileCategoryOpen)}
                className="text-[28px] font-bold text-white hover:scale-110 transition-all cursor-pointer flex items-center gap-1"
                style={{ fontFamily: 'Pacifico, cursive' }}
              >
                Blog
                <i className={`ri-arrow-down-s-line text-white text-xl transition-transform duration-300 ${isMobileCategoryOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isMobileCategoryOpen && (
                <div className="flex flex-col items-center gap-3 mt-3">
                  <a
                    href="/blog"
                    onClick={() => setIsMenuOpen(false)}
                    className="text-base font-semibold text-white/90 hover:text-white hover:scale-105 transition-all cursor-pointer"
                    style={{ fontFamily: 'Nunito, sans-serif' }}
                  >
                    All Posts
                  </a>
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.href}
                      onClick={() => setIsMenuOpen(false)}
                      className="text-base font-semibold text-white/90 hover:text-white hover:scale-105 transition-all cursor-pointer"
                      style={{ fontFamily: 'Nunito, sans-serif' }}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Pets Link */}
            <a
              href="/pets"
              onClick={() => setIsMenuOpen(false)}
              className="text-[28px] font-bold text-white hover:scale-110 transition-all cursor-pointer"
              style={{ fontFamily: 'Pacifico, cursive' }}
            >
              Pets
            </a>

            <a
              href="#contact"
              onClick={() => setIsMenuOpen(false)}
              className="text-[28px] font-bold text-white hover:scale-110 transition-all cursor-pointer"
              style={{ fontFamily: 'Pacifico, cursive' }}
            >
              Contact
            </a>
            <button
              onClick={handleBooking}
              className="bg-white text-[#9B4C9A] px-8 py-3 rounded-full text-lg font-semibold hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
            >
              Book Now
            </button>
          </div>
        </div>
      )}
    </>
  );
}
